import { Link } from 'react-router-dom';

import type { ActivityEvent } from '../../lib/api';
import { formatDateTime } from '../../lib/format';

/**
 * The audit trail, rendered.
 *
 * Two things this deliberately does not do. It does not hide the raw action name — the
 * plain-language label is a convenience, but the underlying `document.restricted_access`
 * is what someone will grep for later, and hiding it makes the trail harder to use than
 * the table it came from. And it does not invent an actor for driver-role events: their
 * identity is masked server-side because a driver's email is driver PII, so those rows say
 * "a driver" and carry a correlation id, rather than quietly showing a blank.
 */

/** Plain-language labels for the actions in services/audit.ts. */
const ACTION_LABEL: Record<string, string> = {
  'auth.login.success': 'Signed in',
  'auth.login.failure': 'Failed sign-in',
  'auth.login.locked': 'Account locked',
  'auth.logout': 'Signed out',
  'auth.signup': 'Workspace created',
  'auth.password.changed': 'Password changed',
  'auth.impersonation.started': 'Milepost opened this workspace',
  'auth.impersonation.ended': 'Milepost left this workspace',
  'admin.tenant.viewed': 'Milepost viewed this account',
  'tenant.updated': 'Workspace settings changed',
  'user.invited': 'User invited',
  'user.deactivated': 'User deactivated',
  'user.role_changed': 'Role changed',
  'driver.created': 'Driver added',
  'driver.updated': 'Driver updated',
  'driver.terminated': 'Driver terminated',
  'document.uploaded': 'Document uploaded',
  'document.viewed': 'Document viewed',
  'document.downloaded': 'Document downloaded',
  'document.accepted': 'Document accepted',
  'document.rejected': 'Document rejected',
  'document.purged_by_retention': 'Purged by retention',
  'document.restricted_access': 'Restricted record access',
  'compliance.evaluated': 'Compliance evaluated',
  'alert.sent': 'Alert sent',
  'alert.bounced': 'Alert bounced',
};

/** Actions worth making visually obvious in a long feed. */
const NOTABLE = new Set([
  'document.restricted_access',
  'auth.impersonation.started',
  'auth.login.locked',
  'alert.bounced',
  'document.purged_by_retention',
]);

interface Props {
  events: ActivityEvent[];
  /** Off on a single-carrier page, where every row is the same carrier. */
  showTenant?: boolean;
}

export function ActivityTable({ events, showTenant = true }: Props) {
  return (
    <div className="card scroll-x">
      <table>
        <thead>
          <tr>
            <th>When</th>
            {showTenant && <th>Carrier</th>}
            <th>Action</th>
            <th>Who</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {events.length === 0 && (
            <tr>
              <td colSpan={showTenant ? 5 : 4} className="muted">
                Nothing recorded.
              </td>
            </tr>
          )}
          {events.map((e) => (
            <tr key={e.id}>
              <td className="tnum">{formatDateTime(e.occurredAt)}</td>
              {showTenant && (
                <td>
                  {e.tenantId ? (
                    <Link to={`/customers/${e.tenantId}`}>{e.tenantName ?? 'Unknown'}</Link>
                  ) : (
                    <span className="muted">Platform</span>
                  )}
                </td>
              )}
              <td>
                <div>
                  {NOTABLE.has(e.action) ? (
                    <strong>{ACTION_LABEL[e.action] ?? e.action}</strong>
                  ) : (
                    (ACTION_LABEL[e.action] ?? e.action)
                  )}
                </div>
                <span className="action-name">{e.action}</span>
              </td>
              <td>
                {e.actorRole === 'driver' ? (
                  // Masked server-side. The correlation id still lets one driver's activity
                  // be followed through a support case without identifying them.
                  <span className="actor-masked" title={e.actorUserId ?? undefined}>
                    a driver
                  </span>
                ) : e.actorEmail || e.actorName ? (
                  <>
                    <div>{e.actorName ?? e.actorEmail}</div>
                    <span className="cite">
                      {e.actorRole?.replace('_', ' ')}
                      {e.onBehalfOf ? ' · via Milepost' : ''}
                    </span>
                  </>
                ) : (
                  <span className="muted">system</span>
                )}
              </td>
              <td className="meta-json">
                {Object.keys(e.metadata ?? {}).length > 0 ? JSON.stringify(e.metadata) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
